/**
 * Mock of `/api/woo/products` and `/api/woo/products/image` for `VITE_MOCK=1` (the real routes
 * are `apps/app/src/routes/wooProducts.ts`). Same answers as the server: editor+ only, a search
 * filters by name, 12 per page, and the image "copy" answers with an address on the pretend
 * media domain. A search for `challenge` shows the Cloudflare-challenge failure and one for
 * `unconfigured` the "shop not connected" one, so those states can be seen without a shop.
 * Demo data only; never used outside mock mode.
 */
import type { Role, ShopProduct } from '../../types/api';
import { MOCK_MEDIA_BASE } from './rehost';
import { swatch } from './media';

const PAGE_SIZE = 12;

function json(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { 'content-type': 'application/json' },
  });
}

const PRODUCTS: ShopProduct[] = [
  ['NoPain Numbing Cream 30 g', '29.00', '', 0, 'instock', '#5457D6'],
  ['NoPain Numbing Cream 50 g', '39.00', '', 0, 'instock', '#0F766E'],
  ['NoPain Aftercare Balm', '19.50', '', 0, 'instock', '#B45309'],
  ['Cream Duo Bundle', '45.50', '45.50', 3, 'onbackorder', '#9333EA'],
  ['Travel Kit', '24.00', '', 2, 'outofstock', '#DB2777'],
].map(([name, price, sale, variations, stock, color], i) => ({
  id: String(101 + i),
  name: name as string,
  price: price as string,
  regularPrice: sale ? '59.00' : (price as string),
  salePrice: sale as string,
  currency: 'EUR',
  // Travel Kit's picture sits on another host, to show the "image could not be copied" note.
  imageUrl:
    name === 'Travel Kit'
      ? 'https://other.example/travel.png'
      : swatch(color as string, (name as string).slice(0, 12)),
  permalink: `https://shop.example/product/${(name as string).toLowerCase().replace(/[^a-z0-9]+/g, '-')}/`,
  stockStatus: stock as ShopProduct['stockStatus'],
  variationsCount: variations as number,
}));

export function handleMockWoo(
  method: string,
  pathname: string,
  params: URLSearchParams,
  body: unknown,
  role: Role,
): Response | null {
  if (pathname === '/woo/products' && method === 'GET') {
    if (role === 'viewer') return json({ error: 'forbidden' }, 403);
    const search = (params.get('search') ?? '').trim().toLowerCase();
    if (search === 'unconfigured') {
      return json(
        {
          error: 'not_configured',
          message:
            'The shop is not connected yet. An admin can connect it under Settings, WooCommerce.',
        },
        409,
      );
    }
    if (search === 'challenge') {
      return json(
        {
          error: 'shop_challenge',
          message:
            "The shop's Cloudflare bot protection blocked the request. Ask whoever runs the shop to let NP-Flow through (a skip rule for /wp-json/np-flow/ or for this app's IP), or fill the block by hand.",
        },
        502,
      );
    }
    const page = Math.max(1, Number(params.get('page')) || 1);
    const matches = PRODUCTS.filter((p) => p.name.toLowerCase().includes(search));
    const slice = matches.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    return json({ products: slice, page, hasMore: matches.length > page * PAGE_SIZE });
  }
  if (pathname === '/woo/products/image' && method === 'POST') {
    if (role === 'viewer') return json({ error: 'forbidden' }, 403);
    const url = (body as { url?: unknown } | null)?.url;
    const product = PRODUCTS.find((p) => p.imageUrl === url);
    if (!product || !String(url).startsWith('data:'))
      return json({ status: 'failed', message: 'This host is not allowed.' });
    return json({ status: 'copied', url: `${MOCK_MEDIA_BASE}/${product.id}.png` });
  }
  return null;
}
